import { supabase } from '../lib/supabase'
import { callRpc, resolveRoleContext, ensureCanManageEvent } from './utils'

/**
 * Fetch dashboard stats via RPC (requires resolved userId only).
 * @param {{ userId?: string, role?: 'student' | 'agent' | 'partner' | 'admin' }} options
 * @returns {Promise<{ data: any, error: any }>}
 */
export const getDashboardStats = async (options = {}) => {
  const { userId, role } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  return callRpc('get_dashboard_stats', {
    user_id: roleContext.userId
  }, 'Unable to load dashboard stats.')
}

/**
 * Fetch event overview stats (paid registrations, revenue, conversion rate) from leads table.
 * Uses leads.payment_status = 'paid' and leads.payment_amount; does not use event_registrations or pipeline status.
 * @param {string} eventId - UUID of the event
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: { paid_registrations: number, revenue: number, total_leads: number, conversion_rate: number } | null, error: any }>}
 */
export const getEventOverviewStats = async (eventId, options = {}) => {
  const access = await ensureCanManageEvent(eventId)
  if (!access.allowed) {
    return { data: null, error: access.error }
  }

  const result = await callRpc('get_event_overview_stats', {
    p_event_id: eventId
  }, 'Unable to load event overview stats.')

  if (result.error) {
    return result
  }

  // RPC returns array of one row
  const row = Array.isArray(result.data) ? result.data[0] : result.data
  if (!row) {
    return {
      data: {
        paid_registrations: 0,
        revenue: 0,
        total_leads: 0,
        conversion_rate: 0
      },
      error: null
    }
  }

  return {
    data: {
      paid_registrations: Number(row.paid_registrations ?? 0),
      revenue: Number(row.revenue ?? 0),
      total_leads: Number(row.total_leads ?? 0),
      conversion_rate: Number(row.conversion_rate ?? 0),
      total_registrations: Number(row.total_registrations ?? 0)
    },
    error: null
  }
}

/**
 * Get upcoming sessions (next N days) for a student across all registered events.
 * Used by student dashboard for "Upcoming sessions" widget.
 * @param {string} studentId
 * @param {number} [days=7]
 * @returns {Promise<{ data: Array<{ session, event }>, error: object | null }>}
 */
export const getUpcomingSessionsForStudent = async (studentId, days = 7) => {
  if (!studentId) return { data: [], error: null }
  const now = new Date()
  const end = new Date(now.getTime() + days * 24 * 60 * 60 * 1000)
  const nowIso = now.toISOString()
  const endIso = end.toISOString()

  const { data: regs, error: regError } = await supabase
    .from('event_registrations')
    .select('event_id')
    .eq('student_id', studentId)

  if (regError || !regs?.length) return { data: [], error: regError ? { message: regError.message, code: regError.code } : null }

  const eventIds = [...new Set(regs.map((r) => r.event_id).filter(Boolean))]
  const { data: sessions, error: sessError } = await supabase
    .from('event_sessions')
    .select('*, event:events(id, title, image_url, featured_image_url, zoom_meeting_link, timezone)')
    .in('event_id', eventIds)
    .gte('start_time', nowIso)
    .lte('start_time', endIso)
    .order('start_time', { ascending: true })

  if (sessError) return { data: [], error: { message: sessError.message, code: sessError.code } }
  return { data: sessions ?? [], error: null }
}
